import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "./SidebarContext";
import { BrandLogos } from "@/components/common/BrandLogos";
import { useT } from "@/i18n/I18nContext";
import { ChevronDown, ChevronRight as Chevron, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import {
  LayoutDashboard,
  Users,
  HeartPulse,
  Building2,
  ShieldCheck,
  Tags,
  ScrollText,
  Activity,
  ClipboardList,
  FilePlus2,
  ListChecks,
  Stethoscope,
  ClipboardCheck,
  HomeIcon,
  Briefcase,
  PhoneCall,
  PhoneIncoming,
  Siren,
  CalendarClock,
  CheckCircle2,
  GitBranch,
  Radio,
  Gauge,
  BellRing,
  SlidersHorizontal,
  Download,
  MapIcon,
  Truck,
  Video,
  ShieldAlert,
  ListTodo,
  UserSquare2,
  Workflow,
  MapPinned,
  Crown,
  Layers,
  FileBarChart,
  Search,
  Route,
  Pill,
  FlaskConical,
  CalendarRange,
  Image as ImageIcon,
  LifeBuoy,
  HelpCircle,
} from "lucide-react";

const ADMIN_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-dashboard" },
  { to: "/users", label: "User Management", icon: Users, testId: "nav-users" },
  { to: "/patients", label: "Patient Management", icon: HeartPulse, testId: "nav-patients" },
  { to: "/facilities", label: "Facility Management", icon: Building2, testId: "nav-facilities" },
  { to: "/roles", label: "Roles & Permissions", icon: ShieldCheck, testId: "nav-roles" },
  { to: "/access-control", label: "Access Control", icon: ShieldAlert, testId: "nav-access-control" },
  { to: "/lookups", label: "Lookups", icon: Tags, testId: "nav-lookups" },
  { to: "/service-catalog", label: "Service Catalog", icon: ClipboardList, testId: "nav-service-catalog" },
  { to: "/drug-formulary", label: "Drug Formulary", icon: Pill, testId: "nav-drug-formulary" },
  { to: "/test-catalog", label: "Lab & Imaging Catalog", icon: FlaskConical, testId: "nav-test-catalog" },
  { to: "/forms", label: "Form Builder", icon: FileBarChart, testId: "nav-forms" },
  { to: "/protocols", label: "Clinical Protocols", icon: GitBranch, testId: "nav-protocols" },
  { to: "/rpm/thresholds", label: "RPM Thresholds", icon: SlidersHorizontal, testId: "nav-rpm-thresholds" },
  { to: "/sms", label: "SMS Console", icon: PhoneCall, testId: "nav-sms" },
  { to: "/callbacks/sla-settings", label: "Callback SLA", icon: CalendarClock, testId: "nav-callback-sla" },
  { to: "/callbacks/questionnaire", label: "Callback Questionnaire", icon: ListChecks, testId: "nav-callback-questionnaire" },
  { to: "/callbacks/escalated", label: "Escalated Callbacks", icon: Siren, testId: "nav-callback-escalated" },
  { to: "/reports", label: "Reports & Exports", icon: Download, testId: "nav-reports" },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, testId: "nav-audit" },
  { to: "/branding", label: "Branding", icon: ImageIcon, testId: "nav-branding" },
  { to: "/admin/faq", label: "FAQ Management", icon: HelpCircle, testId: "nav-admin-faq" },];

const RPM_NAV = {
  section: "RPM Command Center",
  items: [
    { to: "/rpm", label: "Command Center", icon: Radio, exact: true, testId: "nav-rpm" },
    { to: "/rpm/alerts", label: "Active Alerts", icon: BellRing, testId: "nav-rpm-alerts" },
    { to: "/rpm/interventions", label: "Interventions", icon: Gauge, testId: "nav-rpm-interventions" },
  ],
};

const FLEET_NAV = {
  section: "Fleet Operations",
  items: [
    { to: "/fleet", label: "Fleet Dashboard", icon: Briefcase, exact: true, testId: "nav-fleet" },
    { to: "/fleet/orchestrate", label: "Service Assignment", icon: Workflow, testId: "nav-fleet-orchestrate" },
    { to: "/fleet/location-worklist", label: "Location Worklist", icon: MapPinned, testId: "nav-fleet-location-worklist" },
    { to: "/fleet/map", label: "Live Operations Map", icon: MapIcon, testId: "nav-fleet-map" },
    { to: "/fleet/visits", label: "Visit Board", icon: ClipboardList, testId: "nav-fleet-visits" },
    { to: "/fleet/clinicians", label: "Clinicians", icon: Stethoscope, testId: "nav-fleet-clinicians" },
    { to: "/fleet/availability-board", label: "Availability Board", icon: CalendarRange, testId: "nav-fleet-availability-board" },
    { to: "/fleet/vehicles", label: "Vehicles", icon: Truck, adminOnly: true, testId: "nav-fleet-vehicles" },
  ],
};

const TELECONSULT_NAV = {
  section: "Teleconsultation",
  items: [
    { to: "/teleconsult", label: "Consultations", icon: Video, exact: true, testId: "nav-teleconsult" },
    { to: "/availability", label: "Availability", icon: CalendarClock, testId: "nav-tele-availability" },
  ],
};

const HELP_NAV = {
  section: "Help",
  items: [
    { to: "/help/faq", label: "FAQ", icon: HelpCircle, testId: "nav-help-faq" },
    { to: "/support", label: "Contact Support", icon: LifeBuoy, exact: true, testId: "nav-support" },
  ],
};

const CLINICAL_NAV = {
  section: "Clinical Operations",
  items: [
    { to: "/clinical", label: "Active Patients", icon: UserSquare2, exact: true, testId: "nav-clinical" },
    { to: "/clinical/my-patients", label: "My Patients", icon: Stethoscope, testId: "nav-clinical-mine" },
    { to: "/clinical/high-risk", label: "High-Risk Patients", icon: ShieldAlert, testId: "nav-clinical-highrisk" },
    { to: "/care-plans", label: "Care Plans", icon: ClipboardCheck, testId: "nav-clinical-care-plans" },
    { to: "/rpm/alerts?status=escalated", label: "RPM Escalations", icon: Siren, testId: "nav-clinical-rpm-esc" },
    { to: "/hotline/sessions?status=follow_up_required", label: "Hotline Follow-Ups", icon: CalendarClock, testId: "nav-clinical-hotline-fu" },
    { to: "/teleconsult", label: "Upcoming Teleconsultations", icon: Video, testId: "nav-clinical-tele" },
    { to: "/availability", label: "Teleconsult Availability", icon: CalendarClock, testId: "nav-clinical-availability" },
    { to: "/fleet/visits", label: "Home Visits", icon: MapIcon, testId: "nav-clinical-visits" },
    { to: "/my-visits", label: "My Visits", icon: ClipboardList, testId: "nav-clinical-my-visits" },
    { to: "/my-route", label: "My Route", icon: Route, testId: "nav-clinical-my-route" },
    { to: "/clinical/tasks", label: "Clinical Tasks", icon: ListTodo, testId: "nav-clinical-tasks" },
    { to: "/clinical/reminders", label: "Reminders Adherence", icon: ListChecks, testId: "nav-clinical-reminders" },
    { to: "/critical/sos-log", label: "SOS & Critical Log", icon: Siren, testId: "nav-clinical-sos-log" },
    { to: "/callbacks/mine", label: "Callback Requests", icon: PhoneIncoming, testId: "nav-callbacks-mine" },
  ],
};

const COMMAND_NAV = {
  section: "Command Center",
  items: [
    { to: "/command", label: "Executive Dashboard", icon: Crown, exact: true, testId: "nav-command-exec" },
    { to: "/command/operational", label: "Operational Dashboard", icon: LayoutDashboard, testId: "nav-command-ops" },
    { to: "/command/clinical-safety", label: "Clinical Safety", icon: HeartPulse, testId: "nav-command-clinical" },
    { to: "/command/population", label: "Population Health", icon: Layers, testId: "nav-command-pop" },
    { to: "/command/interventions", label: "Active Interventions", icon: ShieldAlert, testId: "nav-command-int" },
    { to: "/command/sla", label: "SLA Manager", icon: GitBranch, testId: "nav-command-sla" },
    { to: "/command/patient-explorer", label: "Patient Explorer", icon: Search, testId: "nav-command-explorer" },
  ],
};

const ONBOARDING_NAV_BY_ROLE = {
  hospital_coordinator: {
    section: "Hospital Workspace",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/new", label: "Create Request", icon: FilePlus2, testId: "nav-ob-new" },
      { to: "/onboarding/requests?scope=involved", label: "My Submissions", icon: ClipboardList, testId: "nav-ob-mine" },
      { to: "/onboarding/requests?status=draft", label: "Drafts", icon: ListChecks, testId: "nav-ob-drafts" },
      { to: "/onboarding/requests?status=information_requested", label: "Information Requested", icon: ListChecks, testId: "nav-ob-info" },
      { to: "/onboarding/requests?status=rejected", label: "Rejected", icon: ListChecks, testId: "nav-ob-rejected" },
      { to: "/onboarding/requests?status=visit_completed", label: "Completed", icon: ListChecks, testId: "nav-ob-completed" },
    ],
  },
  hah_physician: {
    section: "H@H Physician",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/requests?status=under_medical_review", label: "Medical Review Queue", icon: Stethoscope, testId: "nav-ob-queue" },
      { to: "/care-plans", label: "Care Plans", icon: ClipboardCheck, testId: "nav-care-plans" },
      { to: "/onboarding/requests?scope=involved", label: "My Submissions", icon: ClipboardList, testId: "nav-ob-involved" },
      { to: "/onboarding/requests?status=information_requested", label: "Information Requested", icon: ListChecks, testId: "nav-ob-info" },
      { to: "/onboarding/requests?status=rejected", label: "Rejected", icon: ListChecks, testId: "nav-ob-rejected" },
    ],
  },
  physician: {
    section: "H@H Physician",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/requests?status=under_medical_review", label: "Medical Review Queue", icon: Stethoscope, testId: "nav-ob-queue" },
      { to: "/care-plans", label: "Care Plans", icon: ClipboardCheck, testId: "nav-care-plans" },
    ],
  },
  hah_coordinator: {
    section: "H@H Operations",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/requests?status=medical_approved,under_operational_assessment", label: "Assessment Queue", icon: ClipboardCheck, testId: "nav-ob-assess" },
      { to: "/onboarding/requests?status=operationally_approved", label: "Awaiting Assignment", icon: ListChecks, testId: "nav-ob-await" },
      { to: "/onboarding/requests?status=assigned,visit_in_progress", label: "Assigned Visits", icon: ListChecks, testId: "nav-ob-assigned" },
      { to: "/onboarding/requests?status=visit_completed", label: "Visit Findings", icon: ListChecks, testId: "nav-ob-findings" },
      { to: "/onboarding/requests?status=rejected", label: "Rejected", icon: ListChecks, testId: "nav-ob-rejected" },
      { to: "/callbacks/questionnaire", label: "Callback Questionnaire", icon: ListChecks, testId: "nav-callback-questionnaire-co" },
      { to: "/forms", label: "Form Builder", icon: FileBarChart, testId: "nav-forms-hahco" },
    ],
  },
  care_coordinator: {
    section: "H@H Operations",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/requests?status=medical_approved,under_operational_assessment", label: "Assessment Queue", icon: ClipboardCheck, testId: "nav-ob-assess" },
      { to: "/onboarding/requests?status=operationally_approved", label: "Awaiting Assignment", icon: ListChecks, testId: "nav-ob-await" },
      { to: "/callbacks/questionnaire", label: "Callback Questionnaire", icon: ListChecks, testId: "nav-callback-questionnaire-co" },
      { to: "/forms", label: "Form Builder", icon: FileBarChart, testId: "nav-forms-cc" },
    ],
  },
  home_care_team: {
    section: "Field Workspace",
    items: [
      { to: "/onboarding", label: "Visits Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/my-visits", label: "My Visits", icon: ClipboardList, exact: true, testId: "nav-my-visits" },
      { to: "/my-route", label: "My Route", icon: Route, testId: "nav-field-my-route" },
      { to: "/onboarding/requests?status=assigned", label: "Assigned Visits", icon: Briefcase, testId: "nav-ob-assigned" },
      { to: "/onboarding/requests?status=visit_in_progress", label: "In Progress", icon: HomeIcon, testId: "nav-ob-inprogress" },
      { to: "/onboarding/requests?status=visit_completed", label: "Completed Visits", icon: ListChecks, testId: "nav-ob-completed" },
    ],
  },
  hah_technician: {
    section: "Field Workspace",
    items: [
      { to: "/onboarding/requests?statuses=assigned,visit_in_progress,visit_completed,dispositioned", label: "Home Care Visits", icon: LayoutDashboard, testId: "nav-ob-dashboard" },
      { to: "/my-visits", label: "My Visits", icon: ClipboardList, exact: true, testId: "nav-my-visits" },
      { to: "/my-route", label: "My Route", icon: Route, testId: "nav-field-my-route" },
      { to: "/onboarding/requests?status=assigned", label: "Assigned Visits", icon: Briefcase, testId: "nav-ob-assigned" },
      { to: "/onboarding/requests?status=visit_in_progress", label: "In Progress", icon: HomeIcon, testId: "nav-ob-inprogress" },
      { to: "/onboarding/requests?status=visit_completed", label: "Completed Visits", icon: ListChecks, testId: "nav-ob-completed" },
    ],
  },
  hah_physiotherapist: {
    section: "Field Workspace",
    items: [
      { to: "/onboarding/requests?statuses=assigned,visit_in_progress,visit_completed,dispositioned", label: "Home Care Visits", icon: LayoutDashboard, testId: "nav-ob-dashboard" },
      { to: "/my-visits", label: "My Visits", icon: ClipboardList, exact: true, testId: "nav-my-visits" },
      { to: "/my-route", label: "My Route", icon: Route, testId: "nav-field-my-route" },
      { to: "/onboarding/requests?status=assigned", label: "Assigned Visits", icon: Briefcase, testId: "nav-ob-assigned" },
      { to: "/onboarding/requests?status=visit_in_progress", label: "In Progress", icon: HomeIcon, testId: "nav-ob-inprogress" },
      { to: "/onboarding/requests?status=visit_completed", label: "Completed Visits", icon: ListChecks, testId: "nav-ob-completed" },
    ],
  },
  hah_nurse: {
    section: "Nurse Hotline",
    items: [
      { to: "/hotline", label: "Hotline Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-hotline-dashboard" },
      { to: "/hotline/new", label: "New Hotline Session", icon: PhoneIncoming, testId: "nav-hotline-new" },
      { to: "/hotline/sessions", label: "Active Sessions", icon: PhoneCall, testId: "nav-hotline-active" },
      { to: "/hotline/sessions?status=decision_pending", label: "Decision Pending", icon: ListChecks, testId: "nav-hotline-decision" },
      { to: "/hotline/sessions?status=escalated", label: "Escalated", icon: Siren, testId: "nav-hotline-escalated" },
      { to: "/hotline/sessions?status=follow_up_required", label: "Follow-Up Required", icon: CalendarClock, testId: "nav-hotline-fu" },
      { to: "/hotline/sessions?status=closed", label: "Closed Sessions", icon: CheckCircle2, testId: "nav-hotline-closed" },
    ],
  },
};


function isItemActive(location, item) {
  const [path, query] = item.to.split("?");
  if (item.exact) {
    if (location.pathname !== path) return false;
    if (!query) return !location.search || location.search === "";
    return location.search.replace(/^\?/, "") === query;
  }
  if (!location.pathname.startsWith(path)) return false;
  if (query) {
    return location.search.replace(/^\?/, "") === query;
  }
  return true;
}

function NavSection({ title, items, location, collapsed, expanded, onToggle, t }) {
  if (collapsed) {
    return (
      <>
        {items.map((item) => {
          const isActive = isItemActive(location, item);
          const Icon = item.icon;
          return (
            <NavLink key={item.testId + item.to} to={item.to} data-testid={item.testId}
              title={t(item.label)}
              className={`hah-sidebar-item justify-center ${isActive ? "active" : ""}`}>
              <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
            </NavLink>
          );
        })}
        <div className="border-b border-white/10 my-2" />
      </>
    );
  }
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-white/85 hover:text-white text-[13px] uppercase tracking-[0.08em] font-bold"
      >
        <span className="truncate text-left">{t(title)}</span>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 opacity-70 shrink-0" /> : <Chevron className="w-3.5 h-3.5 opacity-70 shrink-0" />}
      </button>
      {expanded && items.map((item) => {
        const isActive = isItemActive(location, item);
        const Icon = item.icon;
        return (
          <NavLink key={item.testId + item.to} to={item.to} data-testid={item.testId}
            className={`hah-sidebar-item ${isActive ? "active" : ""}`}>
            <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
            <span className="truncate">{t(item.label)}</span>
          </NavLink>
        );
      })}
    </div>
  );
}


function useIsDesktop() {
  const [desktop, setDesktop] = useState(
    () => (typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true)
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return desktop;
}


export default function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const { collapsed: deskCollapsed, toggle, mobileOpen, closeMobile } = useSidebar();
  const t = useT();
  const isDesktop = useIsDesktop();
  // Icon-rail collapse only applies on desktop; the mobile drawer always shows full nav.
  const collapsed = deskCollapsed && isDesktop;
  const role = user?.role_code || "viewer";

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { closeMobile(); }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const onboardingNav = ONBOARDING_NAV_BY_ROLE[role];
  const showAdmin = role === "admin";
  const showHotline = ["hah_nurse", "hah_coordinator", "care_coordinator", "admin"].includes(role);
  const hotlineNav = ONBOARDING_NAV_BY_ROLE["hah_nurse"];
  const showRpm = ["admin", "hah_nurse", "hah_coordinator", "hah_physician", "physician", "nurse", "care_coordinator", "clinical_director"].includes(role);
  const showFleet = ["admin", "hah_coordinator", "care_coordinator", "clinical_director", "hah_nurse"].includes(role);
  const showTele = ["admin", "hah_nurse", "hah_coordinator", "hah_physician", "physician", "nurse", "care_coordinator", "clinical_director"].includes(role);
  const showClinical = ["admin", "hah_physician", "physician", "hah_nurse", "nurse", "care_coordinator", "hah_coordinator", "clinical_director"].includes(role);
  const showCommand = ["admin", "clinical_director", "care_coordinator", "hah_coordinator", "nurse", "hah_nurse"].includes(role);

  // Persist accordion state per section across reloads
  const [expandedSections, setExpandedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hah_sidebar_sections") || "null") || { command: true, clinical: true, admin: true }; }
    catch { return { command: true, clinical: true, admin: true }; }
  });
  const toggleSection = (key) => {
    setExpandedSections((s) => {
      const next = { ...s, [key]: !s[key] };
      try { localStorage.setItem("hah_sidebar_sections", JSON.stringify(next)); } catch (_e) { /* ignore */ }
      return next;
    });
  };
  const ex = (k, def = false) => expandedSections[k] ?? def;

  return (
    <aside
      data-testid="sidebar"
      className={`hah-sidebar fixed inset-y-0 left-0 w-64 ${collapsed ? "lg:w-16" : "lg:w-64"} z-40 flex flex-col transition-all duration-200 ${mobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"} lg:translate-x-0`}
    >
      <div className={`px-3 py-4 border-b border-white/10 ${collapsed ? "flex flex-col items-center gap-3" : "flex items-center gap-2"}`}>
        <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
          <Stethoscope className="w-5 h-5 text-white" strokeWidth={2.2} />
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold leading-tight text-[15px] truncate">{t("Hospital at Home")}</div>
            <div className="text-white/70 text-[10px] tracking-wider uppercase truncate">
              {showAdmin ? t("Care Beyond Walls") : (onboardingNav?.section ? t(onboardingNav.section) : (role === "hah_nurse" ? t("Nurse Hotline") : t("Workspace")))}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={toggle}
          data-testid="sidebar-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={
            collapsed
              ? "hidden lg:flex w-10 h-10 items-center justify-center rounded-lg bg-white/15 text-white hover:bg-white/30 ring-1 ring-white/20 hover:ring-white/40 shadow-sm transition"
              : "hidden lg:inline-flex text-white/70 hover:text-white p-1.5 rounded hover:bg-white/10 transition"
          }
        >
          {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={closeMobile}
          data-testid="sidebar-close"
          aria-label="Close menu"
          className="lg:hidden ml-auto text-white/80 hover:text-white p-1.5 rounded hover:bg-white/10 transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {!collapsed && <BrandLogos className="px-3 py-3 border-b border-white/10" />}

      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto"
        onClick={(e) => { if (e.target.closest("a")) closeMobile(); }}>
        {showAdmin && <NavSection title="Admin Console" items={ADMIN_NAV} location={location} collapsed={collapsed} expanded={ex("admin", true)} onToggle={() => toggleSection("admin")} t={t} />}
        {showCommand && (
          <NavSection title={COMMAND_NAV.section} items={COMMAND_NAV.items} location={location} collapsed={collapsed} expanded={ex("command", true)} onToggle={() => toggleSection("command")} t={t} />
        )}
        {showClinical && (
          <NavSection title={CLINICAL_NAV.section} items={CLINICAL_NAV.items} location={location} collapsed={collapsed} expanded={ex("clinical", true)} onToggle={() => toggleSection("clinical")} t={t} />
        )}
        {onboardingNav && role !== "hah_nurse" && (
          <NavSection title={onboardingNav.section} items={onboardingNav.items} location={location} collapsed={collapsed} expanded={ex("onboarding", ["home_care_team", "hah_technician", "hah_physiotherapist"].includes(role))} onToggle={() => toggleSection("onboarding")} t={t} />
        )}
        {showHotline && hotlineNav && (
          <NavSection title="Nurse Hotline" items={hotlineNav.items} location={location} collapsed={collapsed} expanded={ex("hotline")} onToggle={() => toggleSection("hotline")} t={t} />
        )}
        {showRpm && (
          <NavSection title={RPM_NAV.section} items={RPM_NAV.items} location={location} collapsed={collapsed} expanded={ex("rpm")} onToggle={() => toggleSection("rpm")} t={t} />
        )}
        {showFleet && (
          <NavSection title={FLEET_NAV.section} items={FLEET_NAV.items.filter((i) => !i.adminOnly || role === "admin")} location={location} collapsed={collapsed} expanded={ex("fleet")} onToggle={() => toggleSection("fleet")} t={t} />
        )}
        {showTele && (
          <NavSection title={TELECONSULT_NAV.section} items={TELECONSULT_NAV.items} location={location} collapsed={collapsed} expanded={ex("tele")} onToggle={() => toggleSection("tele")} t={t} />
        )}

        {/* Help — visible to every authenticated role */}
        <NavSection title={HELP_NAV.section} items={HELP_NAV.items} location={location} collapsed={collapsed} expanded={ex("help", true)} onToggle={() => toggleSection("help")} t={t} />

        {!showAdmin && !onboardingNav && !showHotline && (
          <NavSection
            title="Workspace"
            location={location}
            collapsed={collapsed}
            expanded={true}
            onToggle={() => {}}
            t={t}
            items={[{ to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" }]}
          />
        )}
      </nav>

      <div className="px-5 py-4 border-t border-white/10 text-white/70 text-[11px]">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-300" />
          <span>{t("System operational")}</span>
        </div>
        <div className="mt-1 text-white/50">v1.2 • Hotline</div>
      </div>
    </aside>
  );
}
